import Vue from "vue";
import _ from "lodash";
import {
  FetchState,
  FetchStateKey,
  HsabHsaAccountTransactionState,
  HsabTransactionDetail,
  TrxDateRange,
  TrxDateRangeType
} from "@/shared/types";
import * as storeHelper from "@/shared/storeHelper";
import * as bankService from "@/shared/api/bankService";
import * as expenseService from "@/shared/api/expenseService";
import * as transactionUtility from "@/employee/Utilities/HsabTransactionUtility";
import { fetchStateKeyMatch } from "@/shared/fetchStateHelper";
import { defineStore } from "pinia";
import { downloadCsvFile } from "@/shared/fileDownload";

import moment from "moment";

const pageSize = 10;

const emptyState: HsabHsaAccountTransactionState = {
  fetchStateDictionary: {},
  fetchKey: null,
  transactionsDictionary: {},
  page: 1,
  transactionFileExportFetchState: FetchState.Uninitialized,
  flowIdAssociations: {}
};

function getFilteredTransactions (state: HsabHsaAccountTransactionState,
                                  dateRange: TrxDateRange,
                                  key: string,
                                  creditDebitIndicator?: string)
    : HsabTransactionDetail[] {
  return _.has(state.transactionsDictionary, dateRange.toString())
      ? transactionUtility.filterTransactions(state.transactionsDictionary[dateRange.toString()], key, creditDebitIndicator)
      : [];
}

function getTransactionsForPage (state: HsabHsaAccountTransactionState,
                                 dateRange: TrxDateRange,
                                 key: string,
                                 creditDebitIndicator?: string)
    : HsabTransactionDetail[] {
  return getFilteredTransactions(state, dateRange, key, creditDebitIndicator)
      .slice((state.page - 1) * pageSize, state.page * pageSize);
}

function getPageCount (state: HsabHsaAccountTransactionState,
                       dateRange: TrxDateRange,
                       key: string,
                       creditDebitIndicator?: string): number {
  return Math.max(Math.ceil(getFilteredTransactions(state, dateRange, key, creditDebitIndicator).length / pageSize), 1);
}

export const useHsabHsaAccountTransactionStore = defineStore("hsabHsaAccountTransaction", {
  state: () => (_.cloneDeep(emptyState)),
  getters: {
    transactions: state => (dateRange: TrxDateRange, key: string) => getTransactionsForPage(state, dateRange, key),
    transactionsPageCount: state => (dateRange: TrxDateRange, key: string) => getPageCount(state, dateRange, key),
    transactionsTotalCount: state =>
        (dateRange: TrxDateRange, key: string) => getFilteredTransactions(state, dateRange, key).length,
    creditTransactions: state => (dateRange: TrxDateRange, key: string) => getTransactionsForPage(state, dateRange, key, "C"),
    creditTransactionsPageCount: state => (dateRange: TrxDateRange, key: string) => getPageCount(state, dateRange, key, "C"),
    creditTransactionsTotalCount: state =>
        (dateRange: TrxDateRange, key: string) => getFilteredTransactions(state, dateRange, key, "C").length,
    debitTransactions: state => (dateRange: TrxDateRange, key: string) => getTransactionsForPage(state, dateRange, key, "D"),
    debitTransactionsPageCount: state => (dateRange: TrxDateRange, key: string) => getPageCount(state, dateRange, key, "D"),
    debitTransactionsTotalCount: state =>
        (dateRange: TrxDateRange, key: string) => getFilteredTransactions(state, dateRange, key, "D").length,
    isLoaded: state => (dateRange: TrxDateRange) => (_.has(state.fetchStateDictionary, dateRange.toString()) ?
        state.fetchStateDictionary[dateRange.toString()] === FetchState.Loaded : FetchState.Uninitialized),
    isTransactionFileExportLoading: state =>
        state.transactionFileExportFetchState === FetchState.Loading ? true : false,
    doesFlowIdHaveAssociation: state => (flowId: string) =>
        state.flowIdAssociations[flowId] !== undefined && state.flowIdAssociations[flowId].hasAnyAssociation,
    getFlowIdAssociations: state => (flowId: string) => state.flowIdAssociations[flowId],
  },
  actions: {
    setFetchState (params: { dateRange: TrxDateRange, fetchState: FetchState, key: FetchStateKey }): void {
      Vue.set(this.fetchStateDictionary, params.dateRange.toString(), params.fetchState);
      this.fetchKey = params.key;
    },
    setPageNumber (page: number): void {
      this.page = page;
    },
    setTransactionsDictionary (params: { dateRange: TrxDateRange, transactions: HsabTransactionDetail[] }): void {
      Vue.set(this.transactionsDictionary, params.dateRange.toString(), params.transactions);
    },
    setTransactionFileExportFetchState (fetchState: FetchState) {
      this.transactionFileExportFetchState = fetchState;
    },
    invalidateFetchState (key: FetchStateKey): void {
      if (fetchStateKeyMatch(key, this.fetchKey)) {
        return;
      }
      const stateClone = _.cloneDeep(emptyState);
      for (const key in stateClone) {
        this[key] = stateClone[key];
      }
    },
    fetchTransactions (params: {
      username: string,
      partnerId: number,
      dateRange: TrxDateRange
    }): Promise<any> {
      const fetchStateKey: FetchStateKey = {
        username: params.username,
        partnerId: params.partnerId
      };

      this.invalidateFetchState(fetchStateKey);

      const dateRange = params.dateRange;

      if (this.fetchStateDictionary[dateRange.toString()] === FetchState.Loaded) {
        return Promise.resolve();
      }
      this.setFetchState({ dateRange, fetchState: FetchState.Loading, key: fetchStateKey });

      const dateFormat = "MM/DD/YYYY";
      let startDate = "";
      let endDate = "";
      const now = moment.now();

      switch (dateRange.variant) {
        case TrxDateRangeType.FullYear:
          startDate = `01/01/${dateRange.year}`;
          endDate = `12/31/${dateRange.year}`;
          break;
        case TrxDateRangeType.TrailingYear:
          startDate = moment(now).subtract(12, "M").format(dateFormat);
          endDate = moment(now).format(dateFormat);
          break;
      }

      return bankService.getHsabTransactions(
          params.username,
          params.partnerId,
          startDate,
          endDate
      ).then(response => {
        if (!fetchStateKeyMatch(this.fetchKey, fetchStateKey)) {
          return;
        }
        this.setTransactionsDictionary({ dateRange, transactions: response.data });
        this.setFetchState({ dateRange, fetchState: FetchState.Loaded, key: fetchStateKey });
      }).catch(err => storeHelper.handleError({ error: err, rethrow: false }));
    },
    downloadTransactionExportFile (params: {
      username: string,
      partnerId: number,
      startYear: number,
      endYear: number
    }): Promise<any> {
      this.setTransactionFileExportFetchState(FetchState.Loading);
      return bankService.getHsabTransactionExportFile(
          params.username,
          params.partnerId,
          params.startYear,
          params.endYear
      ).then(response => {
        downloadCsvFile(response.data, `HSATransactionsAsOf_${moment().format("MMDDYYYY")}.csv`);
        this.setTransactionFileExportFetchState(FetchState.Loaded);
      }).catch(err => {
        storeHelper.handleError({ error: err, rethrow: true });
        this.setTransactionFileExportFetchState(FetchState.Uninitialized);
      });
    },
    checkUncheckedFlowIdsForTransactions (transactions: HsabTransactionDetail[]) {
      const idsToCheck = [];
      for (const transaction of transactions) {
        if (this.flowIdAssociations[transaction.flowId] === undefined) {
          idsToCheck.push(transaction.flowId);
        }
      }

      return this.checkIfFlowIdsHaveTransactionOrReimbursement(idsToCheck);
    },
    checkIfFlowIdsHaveTransactionOrReimbursement (flowIds: string[]) {
      if (flowIds === undefined || flowIds === null || flowIds.length === 0) {
        return Promise.resolve();
      }

      return expenseService.doesFlowIdHaveAssociation(flowIds).then(
          response => {
            if (response.data) {
              for (const flowIdUsageResponse of response.data) {
                this.flowIdAssociations[flowIdUsageResponse.flowId] = flowIdUsageResponse;
              }
            }
          }
      ).catch(err => {
        storeHelper.handleError({ error: err, rethrow: true });
      });
    },
  }
});